import { useEffect, useRef, useState } from "react";
import QRCode from "qrcode";
import { api, type ShowLinkInfo } from "./api";
import "./grading.css";

// Share with family (decision 56, feature showLinks): a teacher-approved, read-only link a family can
// open on a phone. A link keeps a frozen copy of the project, so a newer version needs a new link.
// Any link can be turned off from here.

const DAY = 86_400_000;
const linkUrl = (token: string) => `${window.location.origin}/s/${token}`;
const dayText = (t: number) => new Date(t).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" });
const dateText = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

/** The default end date: May 31, the last day of the school year (next year once June has started). */
export function nextMay31(now = new Date()): string {
  const year = now.getMonth() >= 5 ? now.getFullYear() + 1 : now.getFullYear();
  return `${year}-05-31`;
}

const stateText = (l: ShowLinkInfo) =>
  l.state === "active" ? `Open until ${dayText(l.expiresAt)}` : l.state === "revoked" ? `Turned off ${dayText(l.revokedAt ?? l.expiresAt)}` : `Ended ${dayText(l.expiresAt)}`;

function ShareIcon() {
  return (
    <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="18" cy="5" r="3" />
      <circle cx="6" cy="12" r="3" />
      <circle cx="18" cy="19" r="3" />
      <path d="M8.6 13.5l6.8 4M15.4 6.5l-6.8 4" />
    </svg>
  );
}

export function ShareButton({ projectId, title, disabled, beforeOpen }: { projectId: string; title: string; disabled: boolean; beforeOpen: () => Promise<void> }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" className="pill-dark" title="Share with family" disabled={disabled} onClick={() => setOpen(true)}>
        <ShareIcon />
        <span className="bar-label">Share with family</span>
      </button>
      {open && <ShareDialog projectId={projectId} title={title} beforeOpen={beforeOpen} onClose={() => setOpen(false)} />}
    </>
  );
}

function ShareDialog({ projectId, title, beforeOpen, onClose }: { projectId: string; title: string; beforeOpen: () => Promise<void>; onClose: () => void }) {
  const [linkTitle, setLinkTitle] = useState(title);
  const [expiry, setExpiry] = useState(nextMay31());
  const [links, setLinks] = useState<ShowLinkInfo[] | null>(null);
  const [made, setMade] = useState<ShowLinkInfo | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [review, setReview] = useState<{ path: string; line: number; rule: string; excerpt: string }[]>([]);
  const canvas = useRef<HTMLCanvasElement>(null);
  const first = useRef<HTMLInputElement>(null);

  const load = async () => {
    try {
      setLinks((await api.showLinks()).links.filter((l) => l.projectId === projectId));
    } catch (e) {
      setError((e as Error).message);
    }
  };

  useEffect(() => {
    void load();
    first.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [projectId]);

  // The QR code is drawn once the link exists, so a family can scan it from a printed page.
  useEffect(() => {
    if (!made || !canvas.current) return;
    QRCode.toCanvas(canvas.current, linkUrl(made.token), { width: 200, margin: 2, color: { dark: "#041E42", light: "#FFFFFF" } }).catch(() =>
      setError("The QR code didn't draw. The link still works."),
    );
  }, [made]);

  // checked: the teacher has looked at the lines the code check flagged (see the server's review list).
  const create = async (checked: boolean) => {
    setBusy(true);
    setError(null);
    setCopied(false);
    try {
      await beforeOpen(); // unsaved edits reach the server first, so the snapshot is what's on screen
      const expiresAt = new Date(`${expiry}T23:59:59`).getTime();
      const r = await api.createShowLink(projectId, { title: linkTitle, expiresAt, checked });
      if (!r.link) {
        setReview(r.review);
        return;
      }
      setReview([]);
      setMade(r.link);
      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    if (!made) return;
    try {
      await navigator.clipboard.writeText(linkUrl(made.token));
      setCopied(true);
    } catch {
      setError("Copy didn't work. Select the link and copy it by hand.");
    }
  };

  const revoke = async (token: string) => {
    setError(null);
    try {
      await api.revokeShowLink(token);
      await load();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const today = new Date();
  const oneYear = new Date(Date.now() + 365 * DAY);

  return (
    <div className="dlg-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="dlg" role="dialog" aria-modal="true" aria-labelledby="share-title">
        <div className="dlg-head">
          <span className="eyebrow">Share with family</span>
          <h2 id="share-title">{made ? "Your family link" : "Make a link for families"}</h2>
          {!made && (
            <p className="muted small">
              Anyone with the link can open this project on a phone. It's read-only and shows the project as it is right now. Later changes don't show up, so make a new link to share a newer version.
            </p>
          )}
          {!made && (
            <p className="muted small">
              Before you share, check that the project doesn't show a student's name, photo or email. The computer can't check pictures or words for you.
            </p>
          )}
        </div>

        {made ? (
          <div className="dlg-body share-made">
            <p>
              <b>{made.title}</b> is ready. It's open until {dayText(made.expiresAt)}.
            </p>
            <canvas ref={canvas} className="share-qr" aria-label="QR code for the link" />
            <input className="share-url" readOnly value={linkUrl(made.token)} aria-label="The link" onFocus={(e) => e.currentTarget.select()} />
            <div className="row">
              <button type="button" className="primary small" onClick={copy}>
                {copied ? "Copied" : "Copy link"}
              </button>
              <button type="button" className="linklike" onClick={() => setMade(null)}>
                Make another
              </button>
            </div>
          </div>
        ) : (
          <div className="dlg-body">
            <label className="share-field">
              Title families see
              <input ref={first} value={linkTitle} maxLength={80} onChange={(e) => setLinkTitle(e.target.value)} />
            </label>
            <label className="share-field">
              Turns off on
              <input type="date" value={expiry} min={dateText(today)} max={dateText(oneYear)} onChange={(e) => setExpiry(e.target.value)} />
            </label>
          </div>
        )}

        {review.length > 0 && (
          <section className="share-review" aria-label="Lines to look at before sharing">
            <h3>Look at these before you share</h3>
            <p className="muted small">The code check flagged these. Most are blocked on a family's phone anyway, but take a look first.</p>
            <ul>
              {review.map((f, i) => (
                <li key={i}>
                  <b>{f.path}</b> line {f.line}: {f.rule}
                  <code>{f.excerpt}</code>
                </li>
              ))}
            </ul>
          </section>
        )}

        {error && (
          <p className="dlg-error" role="alert">
            {error}
          </p>
        )}

        <section className="share-list" aria-label="Links for this project">
          <h3>Links for this project</h3>
          {links === null ? (
            <p className="muted small">Loading…</p>
          ) : links.length === 0 ? (
            <p className="muted small">No links yet.</p>
          ) : (
            <ul>
              {links.map((l) => (
                <li key={l.token}>
                  <span className="share-title">{l.title}</span>
                  <span className="muted small">{stateText(l)}</span>
                  {l.state === "active" && (
                    <button type="button" className="linklike" onClick={() => revoke(l.token)}>
                      Turn off
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>

        <div className="dlg-foot">
          {!made && (
            <button type="button" className="primary" disabled={busy || !linkTitle.trim()} onClick={() => create(review.length > 0)}>
              {busy ? "Making the link…" : review.length > 0 ? "I looked. Make the link" : "Create link"}
            </button>
          )}
          <button type="button" className="linklike" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
